/**
 * Sources > Keywords Page (Spec 079)
 *
 * Brand-scoped view of organic Google keywords the brand's domain ranks for,
 * sourced from DataForSEO's ranked_keywords endpoint. Manual refresh + CSV
 * export.
 */

import { useState, useEffect, useCallback } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { apiFetch, apiGet } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Select } from '../components/Select.js';
import { KeywordsUploadModal } from '../components/KeywordsUploadModal.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { OverflowMenu } from '../components/ui/OverflowMenu.js';
import { Banner } from '../components/ui/Banner.js';
import { Link } from 'react-router';
import { useFullBleed } from '../lib/layout-width.js';
import type {
  KeywordsListResponse,
  KeywordsRefreshResponse,
  BrandKeyword,
} from '../types/index.js';

type SortValue = 'volume_desc' | 'volume_asc' | 'keyword_asc' | 'position_asc';

const SORT_OPTIONS: Array<{ value: SortValue; label: string }> = [
  { value: 'volume_desc', label: 'Volume (high → low)' },
  { value: 'volume_asc', label: 'Volume (low → high)' },
  { value: 'keyword_asc', label: 'Keyword (A → Z)' },
  { value: 'position_asc', label: 'Position (best first)' },
];

const PAGE_SIZE = 100;

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Math.round((now - t) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

export function KeywordsPage() {
  useFullBleed();
  const { selectedBrand, selectedBrandTenantId } = useBrand();
  const [rows, setRows] = useState<BrandKeyword[]>([]);
  const [total, setTotal] = useState(0);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortValue>('volume_desc');
  const [offset, setOffset] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);

  const brandId = selectedBrand?.id ?? null;
  const tenantId = selectedBrandTenantId;

  const fetchList = useCallback(async () => {
    if (!brandId || !tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
        sort,
      });
      if (search.trim()) params.set('search', search.trim());
      const result = await apiGet<KeywordsListResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/keywords?${params.toString()}`,
      );
      setRows(result.data);
      setTotal(result.total);
      setLastFetched(result.last_fetched_at);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load keywords');
    } finally {
      setLoading(false);
    }
  }, [brandId, tenantId, sort, search, offset]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Reset pagination when search/sort change
  useEffect(() => {
    setOffset(0);
  }, [search, sort]);

  // Auto-clear toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleRefresh(source: 'ahrefs' | 'dataforseo' = 'ahrefs') {
    if (!brandId || !tenantId) return;
    setRefreshing(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/keywords/refresh?source=${source}`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `${source} refresh failed (${res.status})`);
      }
      const body = (await res.json()) as KeywordsRefreshResponse;
      const sourceLabel = source === 'ahrefs' ? 'Ahrefs' : 'DataForSEO';
      setToast(`Refreshed ${body.data.total} keywords from ${sourceLabel}`);
      setOffset(0);
      await fetchList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }

  async function handleDownload() {
    if (!brandId || !tenantId || !selectedBrand) return;
    setDownloading(true);
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/keywords/export.csv`,
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedBrand.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-keywords.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  if (!selectedBrand) {
    return (
      <div className="hub-page--table" data-density="compact">
        <h1 className="hub-page__title">Keywords</h1>
        <EmptyState
          icon="bx-bulb"
          title="Select a brand"
          description="Choose a brand to view organic keyword coverage."
        />
      </div>
    );
  }

  const hasKeywords = total > 0;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div data-testid="keywords-page" className="hub-page--table" data-density="compact">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Keywords</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
            Organic keywords for {selectedBrand.name}
            {selectedBrand.domain ? ` (${selectedBrand.domain})` : ''}
          </p>
        </div>
        {/* Spec 249 (review P1-2/P1-3): one primary — refresh, from Ahrefs, the
            better source for .dk domains — and everything else behind one
            menu: the DataForSEO fallback, and CSV in and out. With no domain
            the primary stays in its own style, aria-disabled, and the Banner
            below says why and links to the fix; it used to grey out while the
            reason sat in a separate strip. */}
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={() => handleRefresh('ahrefs')}
            disabled={refreshing}
            aria-disabled={!selectedBrand.domain || undefined}
            aria-describedby={!selectedBrand.domain ? 'keywords-no-domain-reason' : undefined}
            data-testid="keywords-refresh"
            title="Fetch organic keywords from Ahrefs"
          >
            {refreshing ? <Spinner /> : <Icon name="bx-refresh" />}
            Refresh keywords
          </Button>
          <OverflowMenu
            label="More actions"
            testId="keywords-more"
            items={[
              {
                label: selectedBrand.domain ? 'Refresh from DataForSEO instead' : 'Refresh from DataForSEO (needs a domain)',
                icon: 'bx-refresh',
                onSelect: () => handleRefresh('dataforseo'),
                disabled: refreshing || !selectedBrand.domain,
                testId: 'keywords-refresh-dataforseo',
              },
              {
                label: 'Upload CSV',
                icon: 'bx-upload',
                onSelect: () => setUploadOpen(true),
                testId: 'keywords-upload',
              },
              {
                label: hasKeywords ? 'Download CSV' : 'Download CSV (no keywords yet)',
                icon: 'bx-download',
                onSelect: handleDownload,
                disabled: downloading || !hasKeywords,
                testId: 'keywords-download',
              },
            ]}
          />
        </div>
      </div>

      <div className="text-xs text-[var(--hub-color-ink-muted)]">
        Last fetched: <span className="font-medium text-[var(--hub-color-ink)]">{formatRelativeTime(lastFetched)}</span>
        {' · refreshes from Ahrefs'}
        {hasKeywords && <> · {total.toLocaleString()} keywords</>}
      </div>

      {/* Spec 248 (review P1-3/P1-4): a Banner, not a filled amber block —
          and it LINKS to the fix it names, which it used to describe in prose. */}
      {!selectedBrand.domain && (
        <Banner tone="warning" title="This brand has no domain set" testId="keywords-no-domain">
          <span id="keywords-no-domain-reason">Keywords are fetched by domain, so refreshing is unavailable until one is set.</span>{' '}
          <Link to="/settings">Add a domain</Link>
        </Banner>
      )}
      {hasKeywords && (
        <p className="text-xs text-[var(--hub-color-ink-muted)]">
          Refresh replaces this brand's keywords from the chosen source. Other sources (e.g. uploaded CSVs) survive. Conflict priority: <strong>Upload &gt; Ahrefs &gt; DataForSEO</strong>.
        </p>
      )}

      {/* Filters sit directly above the rows they filter. */}
      <div className="hub-toolbar">
        {/* Spec 246 (review P0-2): Kridt's Field-with-icon owns the geometry, so the
            text can never start under the glyph. */}
        {/* Spec 249 (review P1-14): every field has a visible label. */}
        <div className="hub-field">
        <label className="hub-field__label" htmlFor="keywords-search">Search</label>
        <span className="hub-input-wrap">
          <Icon name="bx-search" />
          <input
            id="keywords-search"
            type="search"
            placeholder="Keyword contains…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="hub-input"
            data-testid="keywords-search"
          />
        </span>
        </div>
        <div className="hub-field">
        <label className="hub-field__label" htmlFor="keywords-sort">Sort</label>
        <Select
          id="keywords-sort"
          options={SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={sort}
          onChange={(v) => setSort(v as SortValue)}
          placeholder="Sort"
          className="w-56"
        />
        </div>
      </div>

      {toast && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          {toast}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <Spinner className="text-[var(--hub-color-action-text)]" />
        </div>
      )}

      {!loading && !hasKeywords && !error && (
        <EmptyState
          icon="bx-bulb"
          title="No keywords yet"
          description="Refresh keywords to fetch organic keyword data from Ahrefs. DataForSEO is in the menu beside it, as a fallback for broad commercial domains."
        />
      )}

      {!loading && hasKeywords && (
        <>
          <div className="overflow-x-auto">
            <table className="hub-table" data-testid="keywords-table">
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th className="is-num">Volume</th>
                  <th className="is-num">Position</th>
                  <th>URL</th>
                  <th>Intent</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="font-medium">{r.keyword}</td>
                    <td className="is-num">
                      {r.search_volume.toLocaleString()}
                    </td>
                    <td className="is-num">
                      {r.rank_position ?? '--'}
                    </td>
                    <td className="max-w-xs text-xs">
                      {r.ranking_url ? (
                        <a
                          href={r.ranking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[var(--hub-color-action-text)]"
                        >
                          {truncate(r.ranking_url.replace(/^https?:\/\/(www\.)?/, ''), 50)}
                          <Icon name="bx-link-external" />
                        </a>
                      ) : (
                        <span className="text-[var(--hub-color-ink-subtle)]">--</span>
                      )}
                    </td>
                    <td className="text-xs">{r.search_intent ?? '--'}</td>
                    <td className="text-xs">
                      {r.source === 'upload' ? (
                        <span className="hub-chip hub-chip--ok">Upload</span>
                      ) : r.source === 'ahrefs' ? (
                        <span className="hub-chip hub-chip--info">Ahrefs</span>
                      ) : (
                        <span className="hub-chip">DataForSEO</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-[var(--hub-color-ink-muted)]">
            <span>
              Showing {offset + 1}–{pageEnd} of {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {uploadOpen && tenantId && brandId && (
        <KeywordsUploadModal
          tenantId={tenantId}
          brandId={brandId}
          brandName={selectedBrand.name}
          onClose={() => setUploadOpen(false)}
          onUploaded={(result) => {
            setUploadOpen(false);
            setToast(
              `Uploaded ${result.total} keywords` +
                (result.skipped ? ` (${result.skipped} skipped)` : ''),
            );
            setOffset(0);
            void fetchList();
          }}
        />
      )}
    </div>
  );
}
